import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useOffline } from "../../context/OfflineContext";
import { LogOut, User, HelpCircle, Menu, GraduationCap, WifiOff, RefreshCw } from "lucide-react";
import { BASE_DOMAIN } from "../../lib/subdomain";
import { RelojFechaHora } from "../ui/RelojFechaHora";
import { NotificacionesHoy } from "./NotificacionesHoy";

interface TopbarProps {
  onAbrirMenu?: () => void;
}

export function Topbar({ onAbrirMenu }: TopbarProps) {
  const { usuario, logout, reiniciarTour } = useAuth();
  const { enLinea, pendientes, sincronizando, sincronizarAhora } = useOffline();
  const [abierto, setAbierto] = useState(false);

  return (
    <header className="relative h-16 bg-blue-950 flex items-center justify-between gap-1 px-4 sm:px-6 sticky top-0 z-10">
      <button
        onClick={onAbrirMenu}
        className="rounded-xl p-2 text-blue-200 hover:bg-white/10 lg:hidden"
        aria-label="Abrir menú"
      >
        <Menu size={20} />
      </button>
      <RelojFechaHora claro />
      <div className="flex items-center gap-1">
      {!enLinea && (
        <span
          title="Sin conexión — los cambios que hagas se guardarán localmente y se enviarán solos cuando regrese internet."
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
        >
          <WifiOff size={14} /> <span className="hidden sm:inline">Sin conexión — trabajando localmente</span>
        </span>
      )}
      {pendientes > 0 && (
        <button
          onClick={sincronizarAhora}
          disabled={sincronizando || !enLinea}
          title="Cambios guardados sin conexión que faltan por enviarse al servidor"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 disabled:opacity-60"
        >
          <RefreshCw size={13} className={sincronizando ? "animate-spin" : ""} />
          <span className="hidden sm:inline">
            {sincronizando ? "Sincronizando..." : `${pendientes} pendiente${pendientes === 1 ? "" : "s"} de sincronizar`}
          </span>
        </button>
      )}
      <button
        onClick={reiniciarTour}
        title="Ver el tour guiado de nuevo"
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-blue-200/80 hover:bg-white/10 hover:text-white transition-colors"
      >
        <HelpCircle size={16} /> <span className="hidden sm:inline">Ver tour</span>
      </button>
      <a
        href={`https://${BASE_DOMAIN}/tutoriales`}
        target="_blank"
        rel="noreferrer"
        title="Ver videos, guías y preguntas frecuentes"
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-blue-200/80 hover:bg-white/10 hover:text-white transition-colors"
      >
        <GraduationCap size={16} /> <span className="hidden sm:inline">Tutoriales</span>
      </a>
      <NotificacionesHoy claro />
      <div className="relative">
        <button
          onClick={() => setAbierto((v) => !v)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-violet-500 text-white flex items-center justify-center">
            <User size={16} />
          </div>
          <span className="hidden text-sm font-medium text-blue-50 sm:inline">{usuario?.nombre}</span>
        </button>
        {abierto && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1">
            <div className="px-4 py-2 text-xs text-slate-400 border-b border-slate-50">{usuario?.email}</div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut size={16} /> Cerrar sesión
            </button>
          </div>
        )}
      </div>
      </div>
      {/* Línea inferior con un degradado azul→morado que se desliza muy
          lento — el detalle "vivo" que le pidió el negocio a la barra
          superior, sin que llegue a distraer de los datos. */}
      <div className="absolute inset-x-0 bottom-0 h-[2px] sidebar-degradado bg-gradient-to-r from-blue-500 via-violet-400 to-blue-500" />
    </header>
  );
}
